import type { IconMeaning } from '../icons';
import { CLOCK_LOCKOUT_THRESHOLD_KEY, CLOCK_LOCKOUT_WINDOW_KEY, DEFAULT_CLOCK_LOCKOUT_THRESHOLD, DEFAULT_CLOCK_LOCKOUT_WINDOW_MINUTES } from './timeAttendance';
import { canReadModule, canUseCapability, canWriteModule, type GateContext } from '../permissions/guards';
import { isModule } from '../permissions/modules';
import type { Viewer } from '../permissions/viewer';
/**
 * The Settings Index (piece 15): every configurable value collected into one
 * navigation index, organized by category in one fixed display order, so the
 * group column is deterministic wherever the Index renders. 🔨 The Index is an
 * ordinary List Page instance grouping by category through the List Viewer's
 * `groupBy`; every entry is a `SettingsIndexEntry` row and never a `NavItem`.
 */
/**
 * 🔨 The display order (decisions 0427, 1390): the organisation's own facts first,
 * the people in it second, the money third, the sales, field-work and catalog side
 * fourth, the categories nothing has built yet after, then the outside connections
 * and the system. A category is the place a person looks for an entry, never the
 * piece that built it — `ownerPiece` carries that. Company folded into Organization
 * on 2026-09-29, and the tallest tile is what sets how far the page scrolls
 * (decision 0282).
 */
export const SETTINGS_CATEGORIES = [
  'Organization', 'People', 'Security', 'Accounting', 'Banking & Payments', 'Tax', 'CRM',
  // Decision 1390 (T580): the field-work rules, which half-filled Accounting until 2026-09-29.
  'Service',
  // Decision 3940 (T1710): the setup of the field work itself, beginning with the job types.
  'Work',
  'Catalog', 'Documents', 'Terms & Conditions', 'Email', 'Notifications', 'HR',
  // Decision 1202 (T392, P30-1): the quiz question flag's two numbers, after HR because training sits beside it.
  'Knowledge Base',
  'Mobile', 'Modules',
  // Decision 1390 (T580): every connection to an outside service with its key or address.
  'Integrations',
  // Decision 0823 (T271): the system error log, last because nothing in it is configuration somebody sets up.
  'System',
] as const;
export type SettingsCategory = (typeof SETTINGS_CATEGORIES)[number];

/** The glyph each category's tile carries beside its title, from the icon reference (decision 0289). */
export const SETTINGS_CATEGORY_ICONS: Readonly<Record<SettingsCategory, IconMeaning>> = {
  // Decision 0743 (T287): the org chart's own glyph, which its bar row carried until it moved here.
  Organization: 'hr',
  People: 'people',
  Security: 'security',
  Accounting: 'accounting',
  // Decision 0428: the tax register's own glyph, added for a navigation row it no longer has.
  'Banking & Payments': 'bankAccount',
  Tax: 'salesTax',
  CRM: 'crm',
  // Decision 1390: the Work Order's own glyph, the menu's word for the same work.
  Service: 'workOrder',
  // Decision 3940: the dispatch glyph, since Service carries the Work Order's own.
  Work: 'dispatch',
  Catalog: 'catalog',
  Documents: 'documents',
  'Terms & Conditions': 'terms',
  Email: 'mail',
  Notifications: 'notification',
  HR: 'hr',
  // Decision 1202: the knowledge base's own glyph, the one its bar row carries.
  'Knowledge Base': 'knowledgeBase',
  Mobile: 'mobile',
  Modules: 'modules',
  // Decision 1390: the link glyph, for a connection to something outside.
  Integrations: 'link',
  System: 'warning',
};

export const SETTINGS_ROUTE = '/settings';

/**
 * 🔒 The anchor a category's tile carries on the Index, and the address that
 * lands on it (decision 1397). The category half of a settings page's kind line
 * links here, so the way back opens the Index scrolled to the tile the page sits in.
 */
export function settingsCategoryAnchor(category: string): string {
  return `settings-${category.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')}`;
}

export function settingsCategoryRoute(category: string): string {
  return `${SETTINGS_ROUTE}#${settingsCategoryAnchor(category)}`;
}
/** Account Settings, the Record Page over the viewer's own Employment, reached from the avatar menu and not from the Index. */
export const ACCOUNT_SETTINGS_ROUTE = '/settings/account';

export interface SettingsIndexEntryRow {
  id: string;
  category: SettingsCategory;
  title: string;
  route: string;
  /** The capability or Module key the viewer must pass before the row renders; empty is ungated. */
  permissionKey: string | null;
  /** Piece 27, decision 0520: the optional module's key this entry belongs to, and the Index hides it while that module is off. */
  moduleKey?: string | null;
  /** The piece that builds the entry and inserts the row; an entry nobody has built has no row. */
  ownerPiece: number;
  sortRank: number;
  isActive: boolean;
}

/**
 * An Index row's `permissionKey` is one of three shapes (piece 15; decision 0342):
 * a Module the viewer must read, a Module prefixed `write:` the viewer must hold
 * write on, or a capability. 🔨 Every reader of the key resolves it here, so a
 * fourth shape is one edit and not a hunt.
 */
export const WRITE_KEY_PREFIX = 'write:';

export function settingsEntryPasses(viewer: Viewer, key: string | null, ctx: GateContext): boolean {
  if (key === null || key === '') return true;
  if (key.startsWith(WRITE_KEY_PREFIX)) {
    const module = key.slice(WRITE_KEY_PREFIX.length);
    return isModule(module) && canWriteModule(viewer, module, ctx);
  }
  return isModule(key) ? canReadModule(viewer, key, ctx) : canUseCapability(viewer, key, ctx);
}

export function isSettingsCategory(value: string): value is SettingsCategory {
  return (SETTINGS_CATEGORIES as readonly string[]).includes(value);
}

/** The Index's rows in display order: the category's fixed order first, the row's own rank inside it second. */
export function orderIndexEntries<E extends { category: string; sortRank: number; title: string }>(rows: readonly E[]): E[] {
  const rank = (category: string) => {
    const at = (SETTINGS_CATEGORIES as readonly string[]).indexOf(category);
    return at < 0 ? SETTINGS_CATEGORIES.length : at;
  };
  return [...rows].sort((a, b) => rank(a.category) - rank(b.category) || a.sortRank - b.sortRank || a.title.localeCompare(b.title));
}

/** The keys this piece seeds at Organization scope, with the values the piece names (piece 15, Data structures). */
export const RELEASE_DEFAULT_PERMISSION_KEY = 'files.releaseDefaultPermission';
export const RELEASE_DOWNLOAD_OFFERED_KEY = 'files.releaseDownloadOffered';
export const RELEASE_WATERMARK_TEXT_KEY = 'files.releaseWatermarkText';
export const RELEASE_APPROVAL_RULE_KEY = 'files.releaseApprovalRuleId';
export const SHARE_LINK_EXPIRY_DAYS_KEY = 'files.shareLinkExpiryDays';
export const LEGAL_FOOTER_KEY = 'mail.legalFooterHtml';
export const HR_DOCUMENT_RETENTION_YEARS_KEY = 'hr.documentRetentionYears';
/** Piece 34: the five HR day counts, each an effective-dated row on Settings › HR › HR Day Counts (the owner's defaults of 2026-09-30). */
export const HR_CERTIFICATION_EXPIRY_LEAD_DAYS_KEY = 'hr.certificationExpiryLeadDays';
export const HR_RETALIATION_GUARD_DAYS_KEY = 'hr.retaliationGuardDays';
export const HR_ACKNOWLEDGEMENT_REMINDER_DAYS_KEY = 'hr.acknowledgementReminderDays';
export const HR_AWAITING_DELIVERY_STALL_DAYS_KEY = 'hr.awaitingDeliveryStallDays';
export const HR_ABSENCE_ESCALATION_TIMEOUT_DAYS_KEY = 'hr.absenceEscalationTimeoutDays';
/** Piece 34, decisions 0137 and 2680: the named outside contact a complaint about anybody who reads complaints is routed to. */
export const HR_EXTERNAL_ESCALATION_ROUTE_KEY = 'hr.externalEscalationRoute';
/** Piece 34, decision 10652: the substance policy, three lists of lines an administrator fills on HR Rules › Substance Policy. */
export const HR_SUBSTANCE_PROCEDURE_KEY = 'hr.substancePolicy.procedure';
export const HR_SUBSTANCE_CONSEQUENCES_KEY = 'hr.substancePolicy.consequences';
export const HR_SUBSTANCE_TESTING_RULE_KEY = 'hr.substancePolicy.testingRule';
/** Piece 34, decision 12931: how far a gate punch may stand from the site's pin before the approver's queue says so, whole meters. */
export const HR_PUNCH_RADIUS_METERS_KEY = 'hr.punchRadiusMeters';
export const E_SIGNATURE_ENABLED_KEY = 'modules.eSignatureEnabled';
export const BUDGETS_ENABLED_KEY = 'modules.budgetsEnabled';
export const DEPRECIATION_ENABLED_KEY = 'modules.depreciationEnabled';
/**
 * 🔒 Decision 1251: the switch that serves `/dev/reset` and `/dev/outbox` outside
 * development, seeded off. Production never serves them whatever it holds,
 * and both routes ask super admin wherever they answer.
 */
export const DEVELOPER_ROUTES_ENABLED_KEY = 'system.developerRoutesEnabled';

export const PIECE_15_SETTINGS: readonly { key: string; value: string | number | boolean }[] = [
  { key: 'display.dateFormat', value: 'MM/DD/YYYY' },
  { key: RELEASE_DEFAULT_PERMISSION_KEY, value: 'View' },
  { key: RELEASE_DOWNLOAD_OFFERED_KEY, value: true },
  { key: SHARE_LINK_EXPIRY_DAYS_KEY, value: 30 },
  { key: LEGAL_FOOTER_KEY, value: '' },
  { key: E_SIGNATURE_ENABLED_KEY, value: false },
  { key: BUDGETS_ENABLED_KEY, value: false },
  { key: DEPRECIATION_ENABLED_KEY, value: false },
  { key: DEVELOPER_ROUTES_ENABLED_KEY, value: false },
  // Decision 1250: the clock's lockout, five refused punches in fifteen minutes.
  { key: CLOCK_LOCKOUT_THRESHOLD_KEY, value: DEFAULT_CLOCK_LOCKOUT_THRESHOLD },
  { key: CLOCK_LOCKOUT_WINDOW_KEY, value: DEFAULT_CLOCK_LOCKOUT_WINDOW_MINUTES },
];

/*
 * 🔒 Decision 2771 (T1626): every write a person makes to an Organization setting is audited, so no list names
 * the audited keys any more. The release settings, single sign-on, the chain depth, the credit limit, the module
 * switches, the scanner address, the developer routes, the quiz flag, the mail retention, the HR day counts and the
 * seam switches were the 26 such keys before it; Settings › History lists every change since.
 */
